/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { FlaggableType } from "@packages/sdk";
import { useBranch } from "@packages/sdk";
import { useEffect, useState } from "react";

import type { ThemeType } from "../../../../../../global/emotion";
import {
  numericViewportWidth,
  spacing,
  stroke,
} from "../../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../../lib/constants/emotionMediaQueries.constants";
import { FlagRecordButton } from "../../../../blocks/moderation/FlagRecordButton/FlagRecordButton.emotion";
import { CampaignJoinLeaveButton } from "../../../../blocks/primitives/refactor/CampaignJoinLeaveButton/CampaignJoinLeaveButton";
import { ShareCampaignButton } from "../../../../blocks/primitives/ShareCampaignButton/ShareCampaignButton.emotion";
import { ReportIcon, ShareIcon } from "../../../icons/Icon/GeneralIcons";
import { EnterIcon, ExitIcon } from "../../../icons/Icon/NavigationIcons";
import { Cell } from "../../../modals/Cell/Cell.emotion";
import type { ModalDropdownMenuProps } from "../../../modals/ModalDropdownMenu/ModalDropdownMenu.emotion";
import { ModalDropdownMenu } from "../../../modals/ModalDropdownMenu/ModalDropdownMenu.emotion";
import { ModalDropdownMenuItem } from "../../../modals/ModalDropdownMenuItem/ModalDropdownMenuItem.emotion";
import type { CampaignSmallProps } from "../CampaignSmall/CampaignSmall.emotion";
import { CampaignSmall } from "../CampaignSmall/CampaignSmall.emotion";

const buttonResetStyle: Interpolation = {
  border: "none",
  margin: spacing.none,
  textAlign: "left",
};
const campaignSmallStyle: Interpolation = {
  marginBottom: spacing.s,
  paddingLeft: spacing.s,
  paddingRight: spacing.s,
};
const cellReportStyle: Interpolation<ThemeType> = (theme) => ({
  color: `${theme.semanticColors.errorMedium} !important`,
});
const contentStyle: Interpolation<ThemeType> = (theme) => ({
  borderColor: theme.semanticColors.neutralsLowest,
  borderStroke: stroke.regular,
  borderStyle: "solid",
  gap: spacing.none,
  [cssMediaQueryMinWidthTablet]: {
    maxWidth: 360,
    width: numericViewportWidth[100],
  },
  paddingTop: spacing.l,
  width: `calc(${spacing.full} - calc(2 * ${spacing.s}))`,
});
const menuStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  width: spacing.full,
};
const modalOverlayStyle: Interpolation = {
  display: "none",
};

export type CampaignModalDropdownMenuProps = ModalDropdownMenuProps & {
  campaignId: number;
  campaignName?: string;
  campaignSmallProps?: CampaignSmallProps;
  joined?: boolean;
  hasEnded?: boolean;
};

export const CampaignModalDropdownMenu = ({
  campaignId,
  campaignName,
  campaignSmallProps,
  joined,
  hasEnded,
  ...props
}: CampaignModalDropdownMenuProps) => {
  const analytics = useAnalytics();
  const branch = useBranch();
  const [shareUrl, setShareUrl] = useState<string>("");
  const t = useTranslations();

  useEffect(() => {
    branch.campaign({ id: campaignId }).then((url) => setShareUrl(url));
  }, [branch]);

  const itemProps = (option) => {
    return {
      asChild: true,
      onPointerDown: () => {
        analytics.track({
          event: HallowAnalyticsEvent.TappedCampaignOption,
          properties: {
            campaign: campaignId,
            campaign_name: campaignName,
            option: option,
          },
        });
      },
    };
  };

  return (
    <ModalDropdownMenu
      {...props}
      contentCSS={[props.contentCSS, contentStyle]}
      dropdownMenuProps={props.dropdownMenuProps}
      modalProps={{
        modal: false,
        overlayCSS: modalOverlayStyle,
      }}
    >
      <CampaignSmall
        activeState={false}
        focusState={false}
        hoverState={false}
        size="s"
        css={campaignSmallStyle}
        {...campaignSmallProps}
      />
      <div css={menuStyle}>
        <ModalDropdownMenuItem
          dropdownMenuItemProps={{ asChild: true }}
          modalItemProps={{ asChild: true }}
        >
          <Cell
            asChild
            icon={<ShareIcon />}
            text="Share"
            onPointerDown={() => {
              analytics.track({
                event: HallowAnalyticsEvent.ViewedScreen,
                properties: {
                  screen_name: "share_audio_view",
                  type: "campaign",
                },
              });
              // TODO: get this dropdown item working with itemProps() above
              analytics.track({
                event: HallowAnalyticsEvent.TappedCampaignOption,
                properties: {
                  campaign: campaignId,
                  campaign_name: campaignName,
                  option: "share",
                },
              });
            }}
            css={buttonResetStyle}
          >
            <ShareCampaignButton
              shareOptions={{
                color: campaignSmallProps.color,
                description: campaignSmallProps.description,
                imgSrc: campaignSmallProps.imgSrc.large,
                link: shareUrl,
                title: campaignSmallProps.title,
                type: "campaign",
              }}
            />
          </Cell>
        </ModalDropdownMenuItem>
        {!hasEnded && (
          <ModalDropdownMenuItem
            dropdownMenuItemProps={itemProps(joined ? "leave" : "join")}
            modalItemProps={itemProps(joined ? "leave" : "join")}
          >
            <Cell
              asChild
              icon={joined ? <ExitIcon /> : <EnterIcon />}
              text={
                joined ? t("campaign_details_leave") : t("action_join_campaign")
              }
              css={buttonResetStyle}
            >
              <CampaignJoinLeaveButton
                campaignId={campaignId}
                campaignName={campaignName}
                joined={joined}
              />
            </Cell>
          </ModalDropdownMenuItem>
        )}
        <ModalDropdownMenuItem
          dropdownMenuItemProps={itemProps("report")}
          modalItemProps={itemProps("report")}
          options={{ closeModalOnSelect: false }}
        >
          <Cell
            asChild
            icon={<ReportIcon />}
            text={t("dialog_option_report_campaign")}
            css={[buttonResetStyle, cellReportStyle]}
          >
            <FlagRecordButton
              flaggableId={campaignId}
              flaggableType={"campaign" as FlaggableType}
            />
          </Cell>
        </ModalDropdownMenuItem>
      </div>
    </ModalDropdownMenu>
  );
};
